import React, { Suspense, useState, useEffect } from "react";
import { Route, Switch, Redirect } from "react-router-dom";
import Signup from "./Signup";
import LoginTestUser from "./LoginTestUser/LoginTestUser";
import WaitList from "./WaitList";
import AppContainer from "./AppContainer/AppContainer";
import ServerDown from "./ServerDown";
import AddTokens from "./AddTokens";
import TermsAndCondition from "./TermsAndCondition";
import PrivacyPolicy from "./PrivacyPolicy";
import AdminPortal from "./Admin/AdminPortal"; // Make sure this file exists
import VideoPlayer from "./VideoPlayer/VideoPlayer";
import DebugRoute from "./DebugRoute";
import StandaloneFundingFull from "./StandaloneFunding/StandaloneFundingFull";
import MobileLanding from "./MobileLanding";
import DesktopLanding from "./DesktopLanding";
import PublicFundingSheet from "./PublicFundingSheet";
import { shouldShowMobileLanding } from "../utils/mobileDetection";
import LoadingScreen from "../components/LoadingScreen";

import {
  getUserDetail,
  isLoggedInUser,
} from "../constants/function";
import { checkAndRefreshIfNeeded } from "../utils/refreshUserData";
import cookieStorage from "../services/cookie-storage";
import LiveVideoStreaming from "./Streaming";
import AddUsersToStream from './AddUsersToStream/AddUsersToStream';

function isAdminUser() {
  const user = getUserDetail();
  // Check if user has admin role
  return user && user.roles && user.roles.includes('admin');
}

function PrivateRoute({ component: Component, path, appProps, userData: parentUserData, ...rest }) {
  const unauthorisedUrl = ["/", "Signup"];
  const userData = parentUserData || getUserDetail();
  const [isLoadingUserData, setIsLoadingUserData] = useState(false);
  
  
  // console.log('🚪 PrivateRoute triggered for path:', rest.path);
  
  // If user is logged in but no userData, fetch it from API
  useEffect(() => {
    // console.log('🔍 useEffect triggered:', {
    //   isLoggedIn: isLoggedInUser(),
    //   isWhiteListed: isWhiteListed(),
    //   hasUserData: !!userData,
    //   isLoadingUserData
    // });
    
    if (isLoggedInUser() && !userData && !isLoadingUserData) {
      // console.log('🔄 User is logged in but no localStorage data - fetching from API');
      setIsLoadingUserData(true);
      
      // Import ApiService dynamically to avoid circular imports
      import('../services/api-service').then(({ default: ApiService }) => {
        // Add timeout for user profile fetch (10 seconds)
        const timeoutPromise = new Promise((_, reject) => 
          setTimeout(() => reject(new Error('Profile fetch timeout')), 10000)
        );
        
        const fetchProfile = ApiService.getToken().then(() => {
          return ApiService.USER_PROFILE();
        });
        
        Promise.race([fetchProfile, timeoutPromise])
          .then(response => {
            if (response && response.result) {
              console.log('✅ Successfully fetched user data from API');
              localStorage.setItem('userData', JSON.stringify({ userData: response.result }));
              window.location.reload(); // Reload to use the new data
            } else {
              throw new Error('Invalid profile response');
            }
          })
          .catch(error => {
            console.error('❌ Failed to fetch user data:', error);
            // If API call fails or times out, clear everything and redirect to login
            cookieStorage.deleteCookie('Authorization');
            localStorage.clear();
            window.location.href = '/';
          })
          .finally(() => {
            setIsLoadingUserData(false);
          });
      }).catch(error => {
        console.error('Failed to import ApiService:', error);
        // If we can't even load the API service, clear and redirect
        cookieStorage.deleteCookie('Authorization');
        localStorage.clear();
        window.location.href = '/';
        setIsLoadingUserData(false);
      });
    }
  }, [userData, isLoadingUserData]);
  
  const routeDebugInfo = {
    path: rest.path,
    isLoggedIn: isLoggedInUser(),
    // isWhiteListed: removed - no longer needed,
    userDataExists: !!userData,
    hasUserId: !!userData?._id,
    hasBirthYear: !!userData?.birthYear,
    birthYear: userData?.birthYear,
    authCookie: !!cookieStorage.getCookie("Authorization"),
    localStorageExists: !!localStorage.getItem("userData")
  };
  
  // console.log('🔍 PrivateRoute check:', routeDebugInfo);

  return (
    <Route
      {...rest}
      render={(props) => {
        if (
          isLoggedInUser() &&
          unauthorisedUrl.includes(props.location.pathname)
        ) {
          return (
            <Redirect
              to={{
                pathname: `/app/home`,
              }}
            />
          );
        } else if (
          isLoggedInUser() &&
          userData &&
          userData._id && // Make sure we have valid user data
          !userData["birthYear"]
        ) {
          // GUARANTEED: Force redirect to signup
          window.location.href = '/signup';
          return null;
        } else if (isLoggedInUser()) {
          // Show loading while fetching user data
          if (isLoadingUserData) {
            return (
              <LoadingScreen 
                message="Loading user data..." 
                submessage="If this takes too long, you'll be redirected to login"
              />
            );
          }
          return <Component {...props} {...appProps} />;
        } else if (isLoggedInUser() && !userData) {
          // User is authenticated but missing localStorage data - redirect to login to refresh
          // console.log('🔄 Authenticated user missing localStorage data - redirecting to login');
          cookieStorage.deleteCookie('Authorization');
          localStorage.clear();
          return (
            <Redirect
              to={{
                pathname: "/",
              }}
            />
          );
        // Whitelist check removed - no longer needed
        } else {
          return (
            <Redirect
              to={{
                pathname: "/",
              }}
            />
          );
        }
      }}
    />
  );
}

function RouterOutlet(props) {
  const [isReady, setIsReady] = useState(false);
  const [userData, setUserData] = useState(null);
  
  useEffect(() => {
    // Simple approach - just check what we have and move on
    const initializeApp = async () => {
      if (isLoggedInUser()) {
        // Get user data from localStorage
        const data = getUserDetail();
        console.log('RouterOutlet getUserDetail:', data);
        setUserData(data);
      }
      
      // Mark as ready quickly
      setIsReady(true);
    };
    
    initializeApp();
  }, []);
  
  // BLOCK EVERYTHING until ready
  if (!isReady) {
    return <LoadingScreen message="Loading..." />;
  }
  
  return (
    <Suspense fallback={<LoadingScreen />}>
      <Switch>
        <Route
          path="/admin"
          render={(props) => {
            // Special case: Allow funding route with token (bypass auth)
            if (props.location.pathname === '/admin/funding') {
              const urlParams = new URLSearchParams(props.location.search);
              const token = urlParams.get('token');
              if (token === 'siki-investor-2024') {
                return <AdminPortal {...props} />;
              }
            }
            
            // Regular admin auth check
            if (isLoggedInUser() && isAdminUser()) {
              return <AdminPortal {...props} />;
            }
            
            return <Redirect to="/" />;
          }}
        />
      <Route exact path="/" render={(props) => {
        const isLoggedIn = isLoggedInUser();
        // Whitelist no longer needed
        
        // GUARANTEED: If logged in, handle immediately
        if (isLoggedIn) {
          // First try to get userData from localStorage if not in state yet
          const localUserData = userData || getUserDetail();
          
          if (localUserData && localUserData.birthYear) {
            window.location.href = '/app/home'; // Force redirect
            return null;
          } else if (localUserData && !localUserData.birthYear) {
            window.location.href = '/signup'; // Force redirect
            return null;
          } else {
            // No user data yet but logged in - redirect to home and let PrivateRoute handle it
            window.location.href = '/app/home';
            return null;
          }
        }
        
        // Not logged in - show appropriate landing page
        if (shouldShowMobileLanding()) {
          return <MobileLanding {...props} />;
        }
        
        return <DesktopLanding {...props} />;
      }} />
      <Route exact path="/mobile" component={MobileLanding} />
      <Route exact path="/desktop" component={DesktopLanding} />
      <Route exact path="/WaitList"
        render={(props) => <WaitList {...props} />}
      />
      <Route exact path="/TermsAndCondition"
        render={(props) => <TermsAndCondition {...props} />}
      />
      <Route exact path="/PrivacyPolicy"
        render={(props) => <PrivacyPolicy {...props} />}
      />
      <Route exact path="/investor-funding"
        render={(props) => {
          const urlParams = new URLSearchParams(props.location.search);
          const token = urlParams.get('token');
          if (token === 'siki-investor-2024') {
            return <PublicFundingSheet {...props} />;
          }
          return <Redirect to="/" />;
        }}
      />
      <Route exact path="/funding-sheet"
        render={(props) => {
          const urlParams = new URLSearchParams(props.location.search);
          const token = urlParams.get('token');
          if (token === 'siki-investor-2024') {
            return <PublicFundingSheet {...props} />;
          }
          return <Redirect to="/" />;
        }}
      />
      <Route path="/maintenance" component={ServerDown} />
      <Route path="/signup" render={(props) => {
        // GUARANTEED: Check userData from localStorage if state not ready
        if (isLoggedInUser()) {
          const localUserData = userData || getUserDetail();
          
          // If we have complete profile, redirect immediately
          if (localUserData && localUserData.birthYear) {
            window.location.href = '/app/home'; // Force redirect
            return null;
          }
          // User needs to complete profile - show signup page
        }
        
        return <Signup {...props} />;
      }} />
      <Route path="/viaduct/:userNum" component={LoginTestUser} />
      <Route path="/addUsers/:userCount/toStream/:streamId" component={AddUsersToStream} />
      <Route path="/LiveVideoStreaming" component={LiveVideoStreaming} />
      <Route path="/video/:videoId" component={VideoPlayer} />
      <Route path="/debug-route" component={DebugRoute} />

      <Route exact path="/home" render={(props) => {
        // console.log('/home route hit - redirecting to /app/home');
        return <Redirect to="/app/home" />;
      }} />
      
      <PrivateRoute path="/app" component={AppContainer} userData={userData} />
      <PrivateRoute exact path="/add-siki-token" component={AddTokens} userData={userData} />
      
      {/* Fallback route - redirect to signup if no other route matches */}
      <Route render={(props) => {
        // console.log('Fallback route hit for path:', props.location.pathname);
        // Prevent infinite redirects by checking if we're already at root
        if (props.location.pathname === '/') {
          return <Signup {...props} />;
        }
        return <Redirect to="/" />;
      }} />
      </Switch>
    </Suspense>
  );
}

export default RouterOutlet;
